/**
 * The rules behind the admin batch page (`/batches/:id`). Pure, so they are
 * tested without a component tree; every card reads its numbers from here.
 */
import { batchDates, batchSchedule } from "../../Students/StudentProfile/studentRecord";

export { batchDates, batchSchedule };

const DAY_MS = 24 * 60 * 60 * 1000;

const toTime = (value) => {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
};

/**
 * How far through its dates the batch is.
 *
 *   percent    — 0 before the start, 100 after the end, else elapsed / length
 *   phase      — "unscheduled" | "before" | "running" | "after"
 *   daysLeft   — whole days to the end date while running
 *   daysToStart— whole days to the start date before it begins
 */
export function batchTimeline(batch, now = Date.now()) {
  const start = toTime(batch?.startDate);
  const end = toTime(batch?.endDate);
  if (start === null || end === null) {
    return { percent: 0, phase: "unscheduled", daysLeft: null, daysToStart: null };
  }
  if (now < start) {
    return { percent: 0, phase: "before", daysLeft: null, daysToStart: Math.ceil((start - now) / DAY_MS) };
  }
  if (now > end) return { percent: 100, phase: "after", daysLeft: 0, daysToStart: null };
  const percent = end > start ? Math.round(((now - start) / (end - start)) * 100) : 100;
  return { percent, phase: "running", daysLeft: Math.ceil((end - now) / DAY_MS), daysToStart: null };
}

/**
 * Seats taken against the cap. `cap` is null when the batch sets none — a
 * `maxStudents` of 0 means "the plan default", which this page is not told.
 */
export function seatUsage(batch, stats) {
  const used = stats?.activeStudentCount ?? batch?.studentCount ?? batch?.strength ?? 0;
  const cap = batch?.effectiveMaxStudents || batch?.maxStudents || null;
  return { used, cap, percent: cap ? Math.min(100, Math.round((used / cap) * 100)) : null };
}

/**
 * The content-expiry state students are held to. Expiry is enforced only on a
 * COMPLETED batch (`vc_be/utils/batchExpiry.js`), so an earlier date on a
 * running batch is not "expired".
 */
export function contentExpiry(batch, now = Date.now()) {
  const at = toTime(batch?.expiryDate);
  if (at === null) return null;
  return { date: batch.expiryDate, enforced: batch.status === "Completed", expired: batch.status === "Completed" && at < now };
}

/** `yyyy-mm-dd` for a date input, or "" — the shape `<input type="date">` reads. */
export const toDateInput = (value) => {
  const t = toTime(value);
  return t === null ? "" : new Date(t).toISOString().slice(0, 10);
};

/**
 * Who teaches the batch. `instructors[]` is the list; `instructor` is the
 * legacy single field, kept equal to the first — read only when the list is
 * empty. `GET /batches/:id` populates the list with mobile numbers and the
 * legacy field without.
 */
export function batchInstructors(batch) {
  const list = (batch?.instructors || []).filter((i) => i && (i.name || i._id));
  if (list.length) return list;
  return batch?.instructor && (batch.instructor.name || batch.instructor._id) ? [batch.instructor] : [];
}

/**
 * The one lifecycle step this batch is at:
 *   "start"  — not begun: Start batch builds its day-wise syllabus
 *   "end"    — running: End batch completes it
 *   "expiry" — completed: the remaining control is the content expiry date
 */
export function lifecycleStep(batch) {
  if (batch?.status === "Ongoing") return "end";
  if (batch?.status === "Completed") return "expiry";
  return "start";
}

/**
 * A running batch with no day-wise syllabus timeline. It can never be
 * "started" again, so syncing is the only way its students get per-day
 * progress. `synced` is null until the page knows — never prompt on unknown.
 */
export const needsSyllabusSync = (batch, synced) => batch?.status === "Ongoing" && synced === false;

/**
 * Whether to offer "Create Zoom meeting". The server creates one only on Zoom V2
 * with a live connection, so once the institute's settings are known the offer
 * follows them. Settings the caller may not read (403) leave `zoom` null, and
 * the offer then shows for any online batch without a meeting.
 */
export function offerZoomMeeting(batch, zoom) {
  if (!batch || batch.zoomMeetingId) return false;
  if (batch.mode !== "Online" && batch.mode !== "Both") return false;
  if (!zoom) return true;
  return zoom.zoomMode === "v2" && !!zoom.v2Connected;
}

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

/**
 * A student's fee position IN THIS BATCH: their enrolment for it when there is
 * one, else their legacy student-level totals.
 *
 *   { state: "unset" }               — no fee set
 *   { state: "pending", pending }    — something is owed
 *   { state: "paid" }
 */
export function studentFeeInBatch(student, batchId) {
  const ca = (student?.courseAssignments || []).find(
    (a) => sameId(a.batchId?._id ?? a.batchId, batchId),
  );
  const total = Number(ca ? ca.totalFees : student?.totalFees) || 0;
  const pending = Number(ca ? ca.pendingFees : student?.pendingFees) || 0;
  if (total <= 0) return { state: "unset" };
  return pending > 0 ? { state: "pending", pending } : { state: "paid" };
}

/** An instructor's review of one student, or null. The average is to one decimal. */
export function reviewSummary(review) {
  if (!review) return null;
  const performance = Number(review.performance) || 0;
  const participation = Number(review.participation) || 0;
  return {
    average: Math.round(((performance + participation) / 2) * 10) / 10,
    performance,
    participation,
    by: review.instructorId?.name || "",
    remark: review.remark || "",
  };
}

const SKIP_REASON_WORDS = {
  "Already enrolled in this course": "Already enrolled in this course through another batch",
};

/**
 * What `POST /batches/:id/add-students` did, in names. The server reports
 * skipped students by id; the names come from the people the admin picked.
 */
export function addStudentsOutcome(response, picked = []) {
  const byId = new Map(picked.map((s) => [String(s._id), s]));
  const skipped = (response?.skipped || []).map((sk) => ({
    studentId: sk.studentId,
    name: byId.get(String(sk.studentId))?.name || `Student …${String(sk.studentId).slice(-6)}`,
    reason: SKIP_REASON_WORDS[sk.reason] || sk.reason || "Not added",
  }));
  return { added: Number(response?.added) || 0, skipped };
}

/**
 * What `POST /batches/:id/remove-students` did. The server names the students
 * it kept; a name it could not find falls back to the one on screen.
 * `removedIds` are the asked-for ids that left, so the page can untick them.
 */
export function removeStudentsOutcome(response, picked = []) {
  const byId = new Map(picked.map((s) => [String(s._id), s]));
  const skipped = (response?.skipped || []).map((sk) => ({
    studentId: sk.studentId,
    name: sk.name || byId.get(String(sk.studentId))?.name || "",
    mobile: sk.mobile || "",
    reason: sk.reason || "Not removed",
  }));
  const kept = new Set(skipped.map((s) => String(s.studentId)));
  return {
    removed: Number(response?.removed) || 0,
    removedIds: picked.map((s) => String(s._id)).filter((id) => !kept.has(id)),
    skipped,
  };
}

/** Minutes a recorded class ran, or null while it is still open. */
export function sessionMinutes(session) {
  const start = toTime(session?.sessionStart);
  const end = toTime(session?.sessionEnd);
  return start !== null && end !== null ? Math.round((end - start) / 60000) : null;
}

export const presentCount = (session) =>
  (session?.participants || []).filter((p) => p.attendanceStatus === "present").length;

/** A student's attendance share as a tone: 75 % and up is fine, under 50 % is not. */
export const attendanceTone = (percent) => {
  if (percent >= 75) return "success";
  if (percent >= 50) return "warning";
  return "danger";
};
